import { useTheme } from "next-themes";
import { useEffect, useMemo, useState } from "react";
import { VegaEmbed } from "react-vega";
import { useContainerWidth } from "@/lib/hooks/use-container-width";
import type { StackedBarDatum, StackedBarReferenceLine } from "@/lib/utils/vega-chart-shared";
import { createNormalizedStackedBarChartSpec } from "./vega-specs";

type NormalizedStackedBarChartProps = {
  data: StackedBarDatum[];
  /** Stack and legend order, bottom band first. Colours are matched by index. */
  seriesOrder: string[];
  seriesColors: string[];
  width?: number | "container";
  height?: number;
  lang?: string;
  /** False when the caller names the bands itself, e.g. in a table below. */
  showLegend?: boolean;
  /** QL-313 — a dashed horizontal rule at a percent of the bar (a ceiling). */
  referenceLine?: StackedBarReferenceLine;
};

/**
 * Client-side 100%-normalised stacked bar chart (Vega-Lite) — QL-315.
 *
 * Every bar fills the plot, so the chart reads as a mix over time rather than
 * as amounts. The only stacked bar this codebase had before was the
 * two-segment absolute-value cost chart (`MultiBarChart`), which answers a
 * different question.
 */
export function NormalizedStackedBarChart({
  data,
  seriesOrder,
  seriesColors,
  width = "container",
  height = 320,
  lang,
  showLegend = true,
  referenceLine,
}: NormalizedStackedBarChartProps) {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  // Vega-Lite's native `width: "container"` is unreliable here, so measure the
  // wrapper and feed a concrete pixel width into the spec (see useContainerWidth).
  const [containerRef, containerWidth] = useContainerWidth<HTMLDivElement>();
  const effectiveWidth = typeof width === "number" ? width : containerWidth;

  useEffect(() => {
    setMounted(true);
  }, []);

  const isDark = mounted && resolvedTheme === "dark";

  const spec = useMemo(() => {
    if (!data || data.length === 0 || effectiveWidth === null) {
      return null;
    }
    const chartSpec = createNormalizedStackedBarChartSpec(data, {
      seriesOrder,
      seriesColors,
      width: effectiveWidth,
      height,
      isDark,
      lang,
      showLegend,
      referenceLine,
    });
    // Strip any non-plain values before handing the spec to the embed, matching
    // the other chart wrappers.
    return JSON.parse(JSON.stringify(chartSpec));
  }, [
    data,
    seriesOrder,
    seriesColors,
    effectiveWidth,
    height,
    isDark,
    lang,
    showLegend,
    referenceLine,
  ]);

  return (
    <div
      ref={containerRef}
      className="w-full [&_svg]:max-w-full [&_svg]:h-auto"
      style={{ minHeight: height }}
    >
      {spec && <VegaEmbed spec={spec} options={{ actions: false, renderer: "svg" }} />}
    </div>
  );
}
